import { createRouter, createWebHistory } from "vue-router";
import Dashboard from "../views/Dashboard.vue";
import Profile from "../views/Profile.vue";
import Login from "../views/auth/Login.vue";

// A plain string redirect always goes to /login regardless of session state - it
// resolves before router.beforeEach's requiresAuth/isLoggedIn check ever gets a say
// (the guard re-runs against the redirected route, but /login has no requiresAuth
// meta, so there's nothing for it to check). That meant opening the bare admin URL
// (a bookmark, typing the domain, a browser restoring tabs) always bounced straight
// to the login screen even with a fully valid, unexpired session - which is exactly
// what "every time I open the admin panel it asks me to log in again" was. Reading
// localStorage directly here (same key/pattern already used in auth-header.js,
// auth.module.js, and main.js) instead sends an already-logged-in visitor straight
// to /dashboard.
const redirectByAuth = () => (localStorage.getItem("user_free") ? "/dashboard" : "/login");

const routes = [
  {
    path: "/:pathMatch(.*)*",
    redirect: redirectByAuth,
  }
  ,
  {
    path: "/",
    redirect: redirectByAuth,
  },
  {
    path: "/dashboard",
    name: "Dashboard",
    component: Dashboard,
    meta: { requiresAuth: true },
  },
  {
    path: "/order-reports/monthly",
    name: "MonthlyOrderReport",
    component: () => import("../views/Orders/MonthlyReport.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/order-reports",
    name: "OrderReport",
    component: () => import("../views/Orders/DateRangeReport.vue"),
    meta: { requiresAuth: true },
  },

  {
    path: "/login",
    name: "Login",
    component: Login,
  },
  {
    path: "/profile",
    name: "Profile",
    component: Profile,
    meta: { requiresAuth: true },
  },

  //Sections routes
  {
    path: "/testimonials",
    name: "Testimonials",
    component: () => import("../views/Testimonials/List.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/testimonials/create",
    name: "TestimonialCreate",
    component: () => import("../views/Testimonials/Create.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/testimonials/:id/edit",
    name: "TestimonialEdit",
    component: () => import("../views/Testimonials/Edit.vue"),
    props: true,
    meta: { requiresAuth: true },
  },
  //city routes
  {
    path: "/cities",
    name: "Cities",
    component: () => import("../views/Cities/List.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/cities/create",
    name: "CityCreate",
    component: () => import("../views/Cities/Create.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/cities/:id/edit",
    name: "CityEdit",
    component: () => import("../views/Cities/Edit.vue"),
    props: true,
    meta: { requiresAuth: true }
  },
  //blog routes
  {
    path: "/blogs",
    name: "Blogs",
    component: () => import("../views/Blogs/List.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/blogs/create",
    name: "BlogCreate",
    component: () => import("../views/Blogs/Create.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/blogs/:slug/edit",
    name: "BlogEdit",
    component: () => import("../views/Blogs/Edit.vue"),
    props: true,
    meta: { requiresAuth: true },
  },

  //contacts routes
  {
    path: "/contacts",
    name: "Contacts",
    component: () => import("@/views/Contacts/ContactList.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/contacts/:id/view",
    name: "ContactView",
    component: () => import("@/views/Contacts/ContactView.vue"),
    props: true,
    meta: { requiresAuth: true }
  },

  //home banners routes
  {
    path: "/home-banners",
    name: "Home Banners",
    component: () => import("../views/HomeBanners/List.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/home-banners/create",
    name: "HomeBannerCreate",
    component: () => import("../views/HomeBanners/Create.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/home-banners/:id/edit",
    name: "HomeBannerEdit",
    component: () => import("../views/HomeBanners/Edit.vue"),
    props: true,
    meta: { requiresAuth: true }
  },

  //country routes
  {
    path: "/countries",
    name: "Countries",
    component: () => import("../views/Countries/List.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/countries/create",
    name: "CountryCreate",
    component: () => import("../views/Countries/Create.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/countries/:id/edit",
    name: "CountryEdit",
    component: () => import("../views/Countries/Edit.vue"),
    props: true,
    meta: { requiresAuth: true }
  },


  //district routes
  {
    path: "/districts",
    name: "Districts",
    component: () => import("../views/Districts/List.vue"),
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/districts/create",
    name: "DistrictCreate",
    component: () => import("../views/Districts/Create.vue"),
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/districts/:id/edit",
    name: "DistrictEdit",
    component: () => import("../views/Districts/Edit.vue"),
    props: true,
    meta: { requiresAuth: true }
  },
  // categories routes
  {
    path: "/categories",
    name: "Categories",
    component: () => import("../views/Categories/List.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/categories/create",
    name: "CategoryCreate",
    component: () => import("../views/Categories/Create.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/categories/:id/edit",
    name: "CategoryEdit",
    component: () => import("../views/Categories/Edit.vue"),
    props: true,
    meta: { requiresAuth: true }
  },
  // collections routes
  {
    path: "/collections",
    name: "Collections",
    component: () => import("../views/Collections/List.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/collections/create",
    name: "CollectionCreate",
    component: () => import("../views/Collections/Create.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/collections/:id/edit",
    name: "CollectionEdit",
    component: () => import("../views/Collections/Edit.vue"),
    props: true,
    meta: { requiresAuth: true }
  },
  // promo codes routes
  {
    path: "/promo-codes",
    name: "PromoCodes",
    component: () => import("../views/PromoCodes/List.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/promo-codes/create",
    name: "PromoCodeCreate",
    component: () => import("../views/PromoCodes/Create.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/promo-codes/:id/edit",
    name: "PromoCodeEdit",
    component: () => import("../views/PromoCodes/Edit.vue"),
    props: true,
    meta: { requiresAuth: true }
  },
  {
    path: "/fit-profiles",
    name: "FitProfiles",
    component: () => import("../views/FitProfiles/List.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/fit-profiles/create",
    name: "FitProfileCreate",
    component: () => import("../views/FitProfiles/Create.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/fit-profiles/:id/edit",
    name: "FitProfileEdit",
    component: () => import("../views/FitProfiles/Edit.vue"),
    props: true,
    meta: { requiresAuth: true }
  },
  {
    path: "/soles",
    name: "Soles",
    component: () => import("../views/Soles/List.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/soles/create",
    name: "SoleCreate",
    component: () => import("../views/Soles/Create.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/soles/:id/edit",
    name: "SoleEdit",
    component: () => import("../views/Soles/Edit.vue"),
    props: true,
    meta: { requiresAuth: true }
  },
  // SubCategory routes
  {
    path: "/sub-categories",
    name: "SubCategories",
    component: () => import("../views/SubCategories/List.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/sub-categories/create",
    name: "SubCategoryCreate",
    component: () => import("../views/SubCategories/Create.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/sub-categories/:id/edit",
    name: "SubCategoryEdit",
    component: () => import("../views/SubCategories/Edit.vue"),
    props: true,
    meta: { requiresAuth: true }
  },
  // Product routes
  {
    path: "/products",
    name: "Products",
    component: () => import("../views/Products/List.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/products/create",
    name: "ProductCreate",
    component: () => import("../views/Products/Create.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/products/:id/edit",
    name: "ProductEdit",
    component: () => import("../views/Products/Edit.vue"),
    props: true,
    meta: { requiresAuth: true }
  },
  {
    path: "/products/:id/view",
    name: "ProductView",
    component: () => import("../views/Products/View.vue"),
    props: true,
    meta: { requiresAuth: true },
  },
  {
    path: "/products/option-types",
    name: "OptionTypeManagement",
    component: () => import("../views/Products/OptionTypeManagement.vue"),
    meta: { requiresAuth: true, title: 'Manage Option Types' }
  },
  {
    path: "/products/needs-manufacturing",
    name: "NeedsManufacturing",
    component: () => import("../views/Products/NeedsManufacturing.vue"),
    meta: { requiresAuth: true, title: 'Needs Manufacturing' }
  },
  {
    path: "/products/inventory",
    name: "ProductInventory",
    component: () => import("../views/Products/Inventory.vue"),
    meta: { requiresAuth: true, title: 'Inventory' }
  },
  // Product Set Items routes
  {
    path: "/product-set-items",
    name: "ProductSetItems",
    component: () => import("../views/ProductSetItems/List.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/product-set-items/create",
    name: "ProductSetItemCreate",
    component: () => import("../views/ProductSetItems/Create.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/product-set-items/:id/edit",
    name: "ProductSetItemEdit",
    component: () => import("../views/ProductSetItems/Edit.vue"),
    props: true,
    meta: { requiresAuth: true }
  },
  {
    path: "/product-set-items/:id/view",
    name: "ProductSetItemView",
    component: () => import("../views/ProductSetItems/View.vue"),
    props: true,
    meta: { requiresAuth: true },
  },
  {
    path: "/orders",
    name: "OrdersList",
    component: () => import("@/views/Orders/List.vue"),
  },
  {
    // Registered before /orders/:id - a literal path here would otherwise
    // be swallowed by that wildcard (id="create").
    path: "/orders/create",
    name: "OrderCreate",
    component: () => import("@/views/Orders/Create.vue"),
  },
  {
    path: "/orders/:id",
    name: "OrderView",
    component: () => import("@/views/Orders/View.vue"),
  },
  {
    path: "/orders/:id/edit",
    name: "OrderEdit",
    component: () => import("@/views/Orders/Edit.vue"),
  },
  {
    path: "/abandoned-checkouts",
    name: "AbandonedCheckoutsList",
    component: () => import("@/views/AbandonedCheckouts/List.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/abandoned-carts",
    name: "AbandonedCartsList",
    component: () => import("@/views/AbandonedCarts/List.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/bosta-deliveries",
    name: "BostaDeliveriesList",
    component: () => import("@/views/BostaDeliveries/List.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/orders/:id/bosta-delivery",
    name: "CreateBostaDelivery",
    component: () => import("@/views/Orders/CreateBostaDelivery.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/warehouse-stock",
    name: "WarehouseStockUpload",
    component: () => import("@/views/Inventory/WarehouseUpload.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/delivered-orders",
    name: "DeliveredOrdersList",
    component: () => import("@/views/DeliveredOrders/List.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/blocked-contacts",
    name: "BlockedContactsList",
    component: () => import("@/views/BlockedContacts/List.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/whatsapp-conversations",
    name: "WhatsAppConversationsList",
    component: () => import("@/views/WhatsAppConversations/List.vue"),
    meta: { requiresAuth: true },
  },
  //sendEmail route
  {
    path: "/sendEmail",
    name: "SendEmail",
    component: () => import("../views/SendEmail/SendEmail.vue"),
    meta: { requiresAuth: true },
  },

  {
    path: "/subscribers",
    name: "Subscribers",
    component: () => import("../views/Subscribers/List.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/static-pages",
    name: "StaticPages",
    component: () => import("../views/StaticPagesManager.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/trash",
    name: "Trash",
    component: () => import("../views/Trash/List.vue"),
    meta: { requiresAuth: true },
  },

  //users routes
  {
    path: "/banners",
    name: "Banners",
    component: () => import("../views/HomeBanners/List.vue"),
    meta: { requiresAuth: true }
  },
];
const router = createRouter({
  // history: createWebHistory(process.env.BASE_URL),
  history: createWebHistory(),
  routes,
  linkActiveClass: "active",
});

export default router;
